/**
 * Shared render logic for <ry-tile-radio> and <ry-tile-checkbox>.
 * Builds the visually-hidden input + .ry-tile span markup using attributes
 * read off the host element. Not a custom element itself.
 */

import { getIcon } from '../core/ry-icons.js';

export type TileType = 'radio' | 'checkbox';

let tileIdCounter = 0;

function escAttr(v: string): string {
  return v.replace(/"/g, '&quot;');
}
function escText(v: string): string {
  return v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function renderTile(host: HTMLElement, type: TileType): void {
  // Idempotent — bail if already rendered
  if (host.querySelector('[data-ry-target="tile"]')) return;

  const id        = `ry-tile-${++tileIdCounter}`;
  const name      = host.getAttribute('name')      ?? '';
  const value     = host.getAttribute('value')     ?? (type === 'checkbox' ? '1' : '');
  const checked   = host.hasAttribute('checked');
  const disabled  = host.hasAttribute('disabled');
  const iconName  = host.getAttribute('icon')      ?? '';
  const heading   = host.getAttribute('heading')   ?? '';
  const meta      = host.getAttribute('meta')      ?? '';
  const badge     = host.getAttribute('badge')     ?? '';
  const pill      = host.getAttribute('pill')      ?? '';
  const pillText  = host.getAttribute('pill-text') ?? '';
  const warn      = host.hasAttribute('warn');

  const iconHtml = iconName
    ? `<span class="ry-tile__icon">${getIcon(iconName)}</span>`
    : '';

  const pillHtml = (pill && pillText)
    ? ` <span class="ry-tile__pill ry-tile__pill--${escAttr(pill)}">${escText(pillText)}</span>`
    : '';

  const headingHtml = heading
    ? `<span class="ry-tile__heading">${escText(heading)}${pillHtml}</span>`
    : '';

  const metaHtml = meta
    ? `<span class="ry-tile__meta">${escText(meta)}</span>`
    : '';

  const badgeHtml = badge
    ? `<span class="ry-tile__badge">${escText(badge)}</span>`
    : '';

  const tileClasses = ['ry-tile'];
  if (warn) tileClasses.push('ry-tile--warn');

  host.innerHTML = `
    <label for="${id}">
      <input
        type="${type}"
        id="${id}"
        class="ry-tile__input"
        ${name ? `name="${escAttr(name)}"` : ''}
        ${value ? `value="${escAttr(value)}"` : ''}
        ${checked ? 'checked' : ''}
        ${disabled ? 'disabled' : ''}
      >
      <span data-ry-target="tile" class="${tileClasses.join(' ')}">
        ${iconHtml}
        <span class="ry-tile__body">
          ${headingHtml}
          ${metaHtml}
        </span>
        ${badgeHtml}
      </span>
    </label>
  `;
}
